import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  Typography,
  Box,
  Fab,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  InputAdornment,
  Stack,
  Badge,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SendIcon from '@mui/icons-material/Send';
import ForumIcon from '@mui/icons-material/Forum';
import useAPI from '../../../api';
import MessageBox from './MessageBox';

const Message = function Message(
  m,
  previousItem,
  handleReadMessage,
  handleUnreadMessage,
  campaignId
) {
  if (!m) return null;
  const {
    id,
    sender,
    content,
    outbound,
    readByFullName,
    readOn,
    performedOn,
    createdOn,
    status,
    outcome,
    campaignAdvancement,
  } = m || {};

  return (
    <MessageBox
      previousItem={previousItem}
      msgId={id}
      status={status}
      sender={sender}
      value={content}
      outbound={outbound}
      outcome={outcome}
      campaignAdvancement={campaignAdvancement}
      readBy={readByFullName}
      readOn={readOn}
      isRead={readOn}
      performedOn={performedOn || createdOn}
      handleReadMessage={handleReadMessage}
      handleUnreadMessage={handleUnreadMessage}
      campaignId={campaignId}
    />
  );
};

const ChatBox = function ChatBox({
  close,
  handleSendMessage,
  subtractSmsNumber,
  addSmsNumber,
  removeClickedItemToDrawer,
  reAddNotificationToDrawer,
  ObserverWrapper = null,
  recordCountDrawer = 0,
  messages = [],
  campaignId,
  updateItem,
}) {
  const API = useAPI();
  const intl = useIntl();

  const [textValue, setTextValue] = useState('');

  const handleReadMessage = (mId) => {
    API.setMessageRead(
      mId,
      (result) => {
        updateItem(result);
        subtractSmsNumber();
        removeClickedItemToDrawer(mId, recordCountDrawer - 1);
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const handleUnreadMessage = (mId) => {
    API.setMessageUnread(
      mId,
      (result) => {
        addSmsNumber();
        reAddNotificationToDrawer(result, recordCountDrawer + 1);
        updateItem(result);
      },
      (err) => {
        console.log(err);
      }
    );
  };

  return (
    <div
      style={{
        overflow: 'hidden',
        width: '500px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <AppBar
        sx={{
          position: 'relative',
          borderRadius: 'unset',
          color: 'white.main',
        }}
      >
        <Toolbar>
          <ForumIcon />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {intl.messages.CHAT}
          </Typography>
          <IconButton color="inherit" onClick={close}>
            <KeyboardArrowDownIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          flexGrow: 1,
        }}
      >
        <Stack
          spacing={2}
          direction="column-reverse"
          sx={{
            overflow: 'auto',
            pl: '24px',
            pr: '24px',
            flexGrow: 1,
          }}
        >
          <ObserverWrapper
            Component={(m, index) =>
              Message(
                m,
                messages.length > 0 ? messages[index + 1] : {}, //next item
                handleReadMessage,
                handleUnreadMessage,
                campaignId
              )
            }
          />
        </Stack>
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            p: '16px',
            alignItems: 'center',
          }}
        >
          <TextField
            multiline
            sx={{
              flexGrow: 1,
              borderRadius: '10px',
              [`& fieldset`]: {
                borderRadius: '10px',
              },
            }}
            size="small"
            placeholder={intl.messages.WRITE_MESSAGE}
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {textValue.length}
                </InputAdornment>
              ),
            }}
          />

          <Fab
            color="primary"
            size="small"
            disabled={!textValue || textValue === ''}
            onClick={() => {
              handleSendMessage(textValue);
              setTextValue('');
            }}
          >
            <Badge
              badgeContent={
                (textValue && Math.ceil(textValue.length / 160)) || 0
              }
              color="warning"
            >
              <SendIcon />
            </Badge>
          </Fab>
        </Box>
      </div>
    </div>
  );
};

ChatBox.propTypes = {
  close: PropTypes.func.isRequired,
  ObserverWrapper: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  handleSendMessage: PropTypes.func.isRequired,
  subtractSmsNumber: PropTypes.func.isRequired,
  addSmsNumber: PropTypes.func.isRequired,
  removeClickedItemToDrawer: PropTypes.func.isRequired,
  recordCountDrawer: PropTypes.number,
  reAddNotificationToDrawer: PropTypes.func.isRequired,
  messages: PropTypes.array,
};

export default ChatBox;
